import * as React from 'react';
import { faArrowLeft, faArrowRight } from '@awesome.me/kit-4a6bf60416/icons/classic/regular';
import useEmblaCarousel, { type UseEmblaCarouselType } from 'embla-carousel-react';

import { Button } from '@/components/ui/button';
import { Icon } from '@/components/ui/icon';
import type { CarouselNavButtonStyle, CarouselNavPosition } from '@/lib/blocks/collection/blocks';
import { cn } from '@/lib/utils';

type CarouselApi = UseEmblaCarouselType[1];
type UseCarouselParameters = Parameters<typeof useEmblaCarousel>;
type CarouselOptions = UseCarouselParameters[0];
type CarouselPlugin = UseCarouselParameters[1];

type CarouselProps = {
  opts?: CarouselOptions;
  plugins?: CarouselPlugin;
  orientation?: 'horizontal' | 'vertical';
  setApi?: (api: CarouselApi) => void;
};

type CarouselContextProps = {
  carouselRef: ReturnType<typeof useEmblaCarousel>[0];
  api: ReturnType<typeof useEmblaCarousel>[1];
  scrollPrev: () => void;
  scrollNext: () => void;
  canScrollPrev: boolean;
  canScrollNext: boolean;
} & CarouselProps;

const CarouselContext = React.createContext<CarouselContextProps | null>(null);

function useCarousel() {
  const context = React.useContext(CarouselContext);

  if (!context) {
    throw new Error('useCarousel must be used within a <Carousel />');
  }

  return context;
}

/** Skip carousel arrow-key navigation while typing in nested editors or form fields. */
export function shouldIgnoreCarouselKeydown(target: EventTarget | null): boolean {
  if (typeof Element === 'undefined') return false;
  if (!(target instanceof Element)) return false;
  if (target.closest('input, textarea, select, [contenteditable="true"]')) return true;
  if (target.closest('.ProseMirror')) return true;
  return false;
}

function Carousel({
  orientation = 'horizontal',
  opts,
  setApi,
  plugins,
  className,
  children,
  ...props
}: React.ComponentProps<'div'> & CarouselProps) {
  const [carouselRef, api] = useEmblaCarousel(
    {
      ...opts,
      axis: orientation === 'horizontal' ? 'x' : 'y',
    },
    plugins,
  );
  const [canScrollPrev, setCanScrollPrev] = React.useState(false);
  const [canScrollNext, setCanScrollNext] = React.useState(false);

  const onSelect = React.useCallback((api: CarouselApi) => {
    if (!api) return;
    setCanScrollPrev(api.canScrollPrev());
    setCanScrollNext(api.canScrollNext());
  }, []);

  const scrollPrev = React.useCallback(() => {
    api?.scrollPrev();
  }, [api]);

  const scrollNext = React.useCallback(() => {
    api?.scrollNext();
  }, [api]);

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (shouldIgnoreCarouselKeydown(event.target)) return;

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        scrollPrev();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        scrollNext();
      }
    },
    [scrollPrev, scrollNext],
  );

  React.useEffect(() => {
    if (!api || !setApi) return;
    setApi(api);
  }, [api, setApi]);

  React.useEffect(() => {
    if (!api) return;
    onSelect(api);
    api.on('reInit', onSelect);
    api.on('select', onSelect);

    return () => {
      api?.off('select', onSelect);
    };
  }, [api, onSelect]);

  return (
    <CarouselContext.Provider
      value={{
        carouselRef,
        api: api,
        opts,
        orientation: orientation || (opts?.axis === 'y' ? 'vertical' : 'horizontal'),
        scrollPrev,
        scrollNext,
        canScrollPrev,
        canScrollNext,
      }}
    >
      {/* biome-ignore lint/a11y/useAriaPropsSupportedByRole: aria-roledescription is valid on section elements (which have implicit role="region") per ARIA spec. */}
      <section
        onKeyDownCapture={handleKeyDown}
        className={cn('relative', className)}
        aria-roledescription="carousel"
        data-slot="carousel"
        {...props}
      >
        {children}
      </section>
    </CarouselContext.Provider>
  );
}

function CarouselContent({
  className,
  viewportClassName,
  ...props
}: React.ComponentProps<'div'> & { viewportClassName?: string }) {
  const { carouselRef, orientation } = useCarousel();

  return (
    <div
      ref={carouselRef}
      className={cn('overflow-hidden', orientation === 'vertical' && 'h-full', viewportClassName)}
      data-slot="carousel-content"
    >
      <div
        className={cn(
          'flex',
          orientation === 'horizontal' ? '-ml-4' : '-mt-4 flex-col h-full',
          className,
        )}
        {...props}
      />
    </div>
  );
}

function CarouselItem({ className, ...props }: React.ComponentProps<'div'>) {
  const { orientation } = useCarousel();

  return (
    // biome-ignore lint/a11y/useSemanticElements: Carousel items are slides, not form fields. role="group" is appropriate for carousel slides per ARIA patterns.
    <div
      role="group"
      aria-roledescription="slide"
      data-slot="carousel-item"
      className={cn(
        'min-w-0 shrink-0 grow-0 basis-full',
        orientation === 'horizontal' ? 'pl-4' : 'pt-4',
        className,
      )}
      {...props}
    />
  );
}

function CarouselPrevious({
  className,
  variant = 'outline',
  size = 'icon',
  ...props
}: React.ComponentProps<typeof Button>) {
  const { orientation, scrollPrev, canScrollPrev } = useCarousel();

  return (
    <Button
      data-slot="carousel-previous"
      variant={variant}
      size={size}
      className={cn(
        'absolute size-8 rounded-full',
        orientation === 'horizontal'
          ? 'top-1/2 -left-12 -translate-y-1/2'
          : '-top-12 left-1/2 -translate-x-1/2 rotate-90',
        className,
      )}
      disabled={!canScrollPrev}
      onClick={scrollPrev}
      {...props}
    >
      <Icon icon={faArrowLeft} />
      <span className="sr-only">Previous slide</span>
    </Button>
  );
}

function CarouselNext({
  className,
  variant = 'outline',
  size = 'icon',
  ...props
}: React.ComponentProps<typeof Button>) {
  const { orientation, scrollNext, canScrollNext } = useCarousel();

  return (
    <Button
      data-slot="carousel-next"
      variant={variant}
      size={size}
      className={cn(
        'absolute size-8 rounded-full',
        orientation === 'horizontal'
          ? 'top-1/2 -right-12 -translate-y-1/2'
          : '-bottom-12 left-1/2 -translate-x-1/2 rotate-90',
        className,
      )}
      disabled={!canScrollNext}
      onClick={scrollNext}
      {...props}
    >
      <Icon icon={faArrowRight} />
      <span className="sr-only">Next slide</span>
    </Button>
  );
}

function renderNavButton(
  buttonStyle: CarouselNavButtonStyle,
  icon: typeof faArrowLeft,
  label: string,
  onClick: () => void,
  disabled: boolean,
) {
  const variant = buttonStyle === 'solid' ? 'solid' : 'outline';
  const colorScheme = buttonStyle === 'solid' ? ('primary' as const) : undefined;
  return (
    <Button
      variant={variant}
      colorScheme={colorScheme}
      size="icon-sm"
      className="rounded-full"
      disabled={disabled}
      onClick={onClick}
    >
      <Icon icon={icon} />
      <span className="sr-only">{label}</span>
    </Button>
  );
}

function CarouselNavGroup({
  position,
  buttonStyle = 'outline',
  className,
}: {
  position: CarouselNavPosition;
  buttonStyle?: CarouselNavButtonStyle;
  className?: string;
}) {
  const { scrollPrev, scrollNext, canScrollPrev, canScrollNext } = useCarousel();

  const prev = renderNavButton(
    buttonStyle,
    faArrowLeft,
    'Previous slide',
    scrollPrev,
    !canScrollPrev,
  );
  const next = renderNavButton(buttonStyle, faArrowRight, 'Next slide', scrollNext, !canScrollNext);

  if (position === 'bottom-center') {
    return (
      <div className={cn('flex justify-center gap-2 mt-6', className)}>
        {prev}
        {next}
      </div>
    );
  }

  if (position === 'bottom-left') {
    return (
      <div className={cn('flex gap-2 mt-6', className)}>
        {prev}
        {next}
      </div>
    );
  }

  if (position === 'top-right') {
    return (
      <div className={cn('absolute top-0 right-0 flex gap-2 -mt-4', className)}>
        {prev}
        {next}
      </div>
    );
  }
  return null;
}

function CarouselNavControls({
  navPosition,
  navButtonStyle,
  className,
}: {
  navPosition: CarouselNavPosition;
  navButtonStyle: CarouselNavButtonStyle;
  className?: string;
}) {
  if (navPosition === 'sides' || navPosition === 'sides-inset') {
    const variant = navButtonStyle === 'solid' ? 'solid' : 'outline';
    const colorScheme = navButtonStyle === 'solid' ? ('primary' as const) : undefined;
    return (
      <>
        <CarouselPrevious
          className={cn(
            'z-10',
            navPosition === 'sides-inset' ? 'left-2 md:left-4' : '-left-2 md:-left-5',
          )}
          variant={variant}
          colorScheme={colorScheme}
        />
        <CarouselNext
          className={cn(
            'z-10',
            navPosition === 'sides-inset' ? 'right-2 md:right-4' : '-right-2 md:-right-5',
          )}
          variant={variant}
          colorScheme={colorScheme}
        />
      </>
    );
  }
  return (
    <CarouselNavGroup position={navPosition} buttonStyle={navButtonStyle} className={className} />
  );
}

export {
  type CarouselApi,
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
  CarouselNavGroup,
  CarouselNavControls,
};
